// src/components/chat/RequestWindow.jsx - RESPONSIVE VERSION
import React from "react";
import styles from "./RequestWindow.module.css";
import {
  useAcceptChatRequestMutation,
  useRejectChatRequestMutation,
  useGetPendingRequestsQuery,
} from "../../store/api/chatApi";
import { formatLocalDate, formatLocalTime } from "../../utils/dateTime";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

export default function RequestWindow({
  selectedUser,
  onRequestAccepted,
  isMobileVisible = false, // ← NEW
  onMobileBack, // ← NEW
}) {
  const [acceptRequest, { isLoading: isAccepting }] =
    useAcceptChatRequestMutation();
  const [rejectRequest, { isLoading: isRejecting }] =
    useRejectChatRequestMutation();
  const { refetch } = useGetPendingRequestsQuery();
  const navigate = useNavigate();

  const handleAccept = async () => {
    if (!selectedUser?.requestId) return;

    try {
      await acceptRequest(selectedUser.requestId).unwrap();

      if ("Notification" in window && Notification.permission === "granted") {
        new Notification("Chat Request Accepted", {
          body: `You can now chat with ${selectedUser.name}`,
          icon: "/icon.png",
        });
      }

      await refetch();
      onRequestAccepted?.();

      toast.success(
        `Chat request from ${selectedUser.name} accepted! You can now message each other.`,
      );
    } catch (error) {
      console.error("Failed to accept request:", error);
      toast.error("Failed to accept request. Please try again.");
    }
  };

  const handleDecline = async () => {
    if (!selectedUser?.requestId) return;

    if (
      !window.confirm(
        `Are you sure you want to decline the request from ${selectedUser.name}?`,
      )
    )
      return;

    try {
      await rejectRequest(selectedUser.requestId).unwrap();

      // Optional notification
      if ("Notification" in window && Notification.permission === "granted") {
        new Notification("Chat Request Declined", {
          body: `You declined ${selectedUser.name}'s request`,
          icon: "/icon.png",
        });
      }

      await refetch();
      onRequestAccepted?.(); // refresh UI / sidebar
    } catch (error) {
      console.error("Failed to decline request:", error);
      toast.error("Failed to decline request. Please try again.");
    }
  };

  const handleViewProfile = () => {
    if (!selectedUser?.id) return;
    navigate(`/profile/${selectedUser.id}`);
  };

  /* ─── Empty state ─── */
  if (!selectedUser) {
    return (
      <div
        className={[
          styles.requestWindow,
          isMobileVisible ? styles.panelVisible : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>📬</div>
          <h3>Select a request</h3>
          <p>Choose a request from the sidebar to view details</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={[
        styles.requestWindow,
        isMobileVisible ? styles.panelVisible : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {/* Header */}
      <div className={styles.header}>
        {/* ← Back button (mobile only) */}
        <button
          className={styles.backBtn}
          onClick={onMobileBack}
          aria-label="Back"
        >
          ‹
        </button>

        <div className={styles.userInfo}>
          {selectedUser.profilePhotoUrl ? (
            <img
              src={selectedUser.profilePhotoUrl}
              alt={selectedUser.name}
              className={styles.avatar}
              onClick={handleViewProfile}
              style={{ cursor: "pointer" }}
              title="View profile"
            />
          ) : (
            <div
              className={styles.avatarPlaceholder}
              onClick={handleViewProfile}
              title="View profile"
            >
              {selectedUser.name?.charAt(0).toUpperCase() || "?"}
            </div>
          )}
          <div>
            <h2
              className={styles.userName}
              onClick={handleViewProfile}
              title="View profile"
            >
              {selectedUser.name}
            </h2>
            <p className={styles.requestTime}>
              Sent {formatLocalDate(selectedUser.lastSentAt)}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={styles.content}>
        <div className={styles.requestCard}>
          <div className={styles.requestHeader}>
            <span className={styles.requestIcon}>📨</span>
            <h3>Chat Request</h3>
          </div>

          <div className={styles.requestBody}>
            <p className={styles.requestMessage}>
              {selectedUser.lastMessage || "Wants to connect with you"}
            </p>
            {selectedUser.lastSentAt && (
              <p className={styles.requestMeta}>
                <span className={styles.timeIcon}>🕐</span>
                {formatLocalTime(selectedUser.lastSentAt)}
              </p>
            )}
          </div>

          <div className={styles.requestInfo}>
            <div className={styles.infoItem}>
              <span className={styles.infoIcon}>ℹ️</span>
              <p>
                If you accept this request, you and {selectedUser.name} will be
                able to send messages to each other.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className={styles.actions}>
          <button
            className={styles.acceptBtn}
            onClick={handleAccept}
            disabled={isAccepting || isRejecting}
          >
            {isAccepting ? (
              <>
                <span className={styles.spinner}></span>Accepting...
              </>
            ) : (
              <>
                <span className={styles.btnIcon}>✓</span>Accept Request
              </>
            )}
          </button>

          <button
            className={styles.declineBtn}
            onClick={handleDecline}
            disabled={isAccepting || isRejecting}
          >
            {isRejecting ? (
              <>
                <span className={styles.spinner}></span>Declining...
              </>
            ) : (
              <>
                <span className={styles.btnIcon}>✕</span>Decline
              </>
            )}
          </button>

          <button className={styles.viewProfileBtn} onClick={handleViewProfile}>
            <span className={styles.btnIcon}>👤</span>View Profile
          </button>
        </div>

        <div className={styles.footer}>
          <p className={styles.footerText}>
            💡 <strong>Tip:</strong> Check their profile before accepting to
            make sure you want to connect.
          </p>
        </div>
      </div>
    </div>
  );
}
